<template>
  <div class="workspace-documents-editable-tab">
    <div class="workspace-documents-editable-tab__actions">
      <vf-button
        v-if="canAddDocumentsToWorkspaces"
        data-testid="upload-document"
        :variant="ButtonVariant.SECONDARY"
        @click="emitUploadDocument"
      >
        {{ t('workspaceDocuments.uploadDocument') }}
      </vf-button>
      <vf-button
        v-if="canAddDocumentsToWorkspaces"
        data-testid="add-for-editing"
        @click="emitAddForEditing"
      >
        {{ t('workspaceDocuments.addForEditing') }}
      </vf-button>
    </div>
    <div class="scrollbar-to-top">
      <table-wrapper
        v-model:filters="filters"
        v-model:page-options="pageOptions"
        :fields="tableFields"
        :filter-configuration="filterConfiguration"
        :loading="isLoading"
        :items="documents"
        :mode="TableMode.LAZY"
        row-clickable
        @row-click="goToWorkspaceDocumentDetailView"
      >
        <template #cell(renditionFilePresent)="{ data }">
          <font-awesome-icon
            v-if="data?.renditionFilePresent"
            :icon="faCheck"
          />
          <font-awesome-icon v-else :icon="faXmark" />
        </template>
        <template #cell(involvementType)="{ data }">
          <div
            v-if="data?.involvementType"
            :class="getInvolvementTypeClass(data.involvementType)"
            class="involvement-type-cell"
          >
            <font-awesome-icon
              :icon="getInvolvementTypeIcon(data.involvementType)"
            />
            <span>
              {{
                t(`workspaceDocumentInvolvementType.${data.involvementType}`)
              }}
            </span>
          </div>
        </template>
      </table-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useNavigator } from '@/composables/useNavigator';
  import { useRoles } from '@/composables/useRoles';
  import { useToast } from '@/composables/useToast';
  import { endpointsList, formatDateForDisplay } from '@/helpers';
  import axios, { AxiosResponse } from 'axios';
  import {
    IWorkspaceDocumentsListFilters,
    IWorkspaceDocumentsListItemDto,
    IWorkspaceDocumentsListPageDto,
    IWorkspaceDocumentsListPageOptionsDto,
    Order,
    WorkflowStatus,
    WorkspaceDocumentInvolvementType,
  } from 'types';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '../TableWrapper';
  import { EDITABLE_TABLE_NAMESPACE } from './constants';
  import { WorkspaceDocumentsEmbeddedTab } from './WorkspaceDocuments.types';
  import { ButtonVariant } from '../VfButton/VfButton.types';
  import {
    faCheck,
    faFile,
    faFileLines,
    faPenToSquare,
    faXmark,
  } from '@fortawesome/free-solid-svg-icons';
  import { faFileCad } from '@fortawesome/pro-solid-svg-icons';

  const props = defineProps<{
    workspaceId: string;
  }>();

  const emit = defineEmits<{
    (e: 'upload-document'): void;
    (e: 'add-for-editing'): void;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { goToWorkspaceDocumentDetailView: navigateToWorkspaceDocument } =
    useNavigator();
  const { canManageWorkspaceDocuments: canAddDocumentsToWorkspaces } =
    useRoles();

  const documents = ref<IWorkspaceDocumentsListItemDto[]>([]);

  const { isLoading, pageOptions, filters, filterParams } = useTableWrapper<
    IWorkspaceDocumentsListItemDto,
    IWorkspaceDocumentsListPageOptionsDto
  >(
    TableMode.LAZY,
    'lastModified',
    getWorkspaceDocuments,
    Order.DESC,
    true,
    EDITABLE_TABLE_NAMESPACE,
  );

  const tableFields = computed(() =>
    new TableFieldsBuilder<IWorkspaceDocumentsListItemDto>({
      labelTranslationKey: 'workspaceDocuments',
      sortable: true,
    })
      .addField('thumbnail', {
        sortable: false,
        label: t('workspaceDocuments.preview'),
        width: '8%',
        icon: faFile,
      })
      .addField('title', {
        sortable: true,
        label: t('workspaceDocuments.columnTitle'),
        width: '12%',
        fluid: true,
      })
      .addField('kks', {
        sortable: false,
        label: t('workspaceDocuments.kks'),
        width: '8%',
        fluid: true,
      })
      .addField('documentClass', {
        sortable: false,
        label: t('workspaceDocuments.dcc'),
        width: '20%',
        fluid: true,
      })
      .addField('renditionFilePresent', {
        width: '5%',
        icon: faFileCad,
        sortable: false,
      })
      .addField('involvementType', {
        sortable: false,
        label: t('workspaceDocuments.involvementType'),
        width: '16%',
        valueTranslationKey: 'workspaceDocumentInvolvementType',
        fluid: true,
      })
      .addField('version', {
        sortable: true,
        label: t('workspaceDocuments.version'),
        width: '8%',
      })
      .addField('lastModified', {
        sortable: true,
        label: t('workspaceDocuments.lastModified'),
        width: '12%',
        valueGetter: (item) =>
          item.lastModified
            ? formatDateForDisplay(new Date(item.lastModified))
            : '',
      })
      .addField('workflowStatus', {
        sortable: false,
        label: t('workspaceDocuments.workflowStatus'),
        width: '18%',
        valueTranslationKey: 'workflowStatus',
      })
      .build(),
  );

  const filterConfiguration = computed(() =>
    new TableFiltersBuilder<
      IWorkspaceDocumentsListItemDto,
      IWorkspaceDocumentsListFilters
    >({
      translationKey: 'workspaceDocuments',
    })
      .addInputFilter('title', {
        width: '200px',
        simpleLabel: true,
      })
      .addInputFilter('kks', {
        simpleLabel: true,
      })
      .addInputFilter('documentClass', {
        simpleLabel: true,
        customLabel: t('workspaceDocuments.dcc'),
      })
      .addSelectFilter('involvementType', {
        selectOptions: Object.values(WorkspaceDocumentInvolvementType),
        translationKey: 'workspaceDocumentInvolvementType',
      })
      .addInputFilter('version', {
        width: '120px',
        simpleLabel: true,
      })
      .addSelectFilter('workflowStatus', {
        selectOptions: Object.values(WorkflowStatus).filter(
          (status) => status !== WorkflowStatus.APPROVED,
        ),
        translationKey: 'workflowStatus',
      })
      .addDateRangeFilter('lastModified', {
        dateFromKey: 'lastModifiedFrom',
        dateToKey: 'lastModifiedTo',
        simpleLabel: true,
      })
      .build(),
  );

  function emitUploadDocument(): void {
    emit('upload-document');
  }

  function emitAddForEditing(): void {
    emit('add-for-editing');
  }

  async function getWorkspaceDocuments(): Promise<void> {
    try {
      const response: AxiosResponse<IWorkspaceDocumentsListPageDto> =
        await axios.get(
          endpointsList.workspaceUploads.getWorkspaceDocuments(
            props.workspaceId,
            {
              order: pageOptions.value.sortOrder,
              page: pageOptions.value.page,
              sortBy: pageOptions.value.sortBy,
              take: pageOptions.value.perPage,
              includeThumbnail: true,
              ...filterParams.value,
            },
          ),
        );
      documents.value = response.data.data;
      pageOptions.value.rows = response.data.meta.itemCount;
    } catch {
      createErrorToast(t('workspaceDocuments.errors.errorGettingDocuments'));
    }
  }

  function goToWorkspaceDocumentDetailView(
    data: IWorkspaceDocumentsListItemDto,
  ): void {
    navigateToWorkspaceDocument(
      props.workspaceId,
      data.documentId,
      WorkspaceDocumentsEmbeddedTab.EDITABLE,
    );
  }

  function getInvolvementTypeIcon(type: WorkspaceDocumentInvolvementType) {
    switch (type) {
      case WorkspaceDocumentInvolvementType.CHECKED_OUT:
        return faPenToSquare;
      case WorkspaceDocumentInvolvementType.CREATED:
      default:
        return faFileLines;
    }
  }

  function getInvolvementTypeClass(type: WorkspaceDocumentInvolvementType) {
    switch (type) {
      case WorkspaceDocumentInvolvementType.CHECKED_OUT:
        return 'involvement-type--checked-out';
      case WorkspaceDocumentInvolvementType.CREATED:
      default:
        return 'involvement-type--created';
    }
  }

  defineExpose({
    refreshDocuments: getWorkspaceDocuments,
  });
</script>

<style scoped lang="scss">
  .workspace-documents-editable-tab__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .involvement-type-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
  }

  .involvement-type--created {
    color: $spree-blue-dark-1;
  }

  .involvement-type--checked-out {
    color: $primary-color;
  }

  .involvement-type--read-only {
    color: $inactive-font-color;
  }
</style>
